import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, finalize, map, switchMap, takeUntil } from 'rxjs/operators';

import { UsersService } from '../../users.service';
import { SectorsService } from '../../../sectors/sectors.service';
import { BranchNames, SectorNames } from '../../../sectors/sectors';
import { Region } from '../../../normatives/normatives';
import { IQualification, IQualificationEmployeeRow, IUserQualification } from '../../users';
import { QUALIFICATION_SUBTYPES } from './qualification-subtypes';

const NO_SUBTYPES: string[] = [];
// Фильтр списка сотрудников на вкладке: groups_ids=4&employee_status=1
const QUALIFICATION_EMPLOYEES_GROUP_ID = 4;
const QUALIFICATION_EMPLOYEE_STATUS = 1;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-users-qualification',
  templateUrl: './qualification.component.html',
  styleUrls: ['./qualification.component.scss'],
  standalone: false,
})
export class UsersQualificationComponent implements OnInit, OnDestroy {
  public regions: Region[] = [];
  public branches: BranchNames[] = [];
  public sectors: SectorNames[] = [];

  public selectedRegion: Region | null = null;
  public selectedBranch: BranchNames | null = null;
  public selectedSector: SectorNames | null = null;

  public appliedRegion: Region | null = null;
  public appliedBranch: BranchNames | null = null;
  public appliedSector: SectorNames | null = null;

  public qualifications: IQualification[] = [];
  public employeeRows: IQualificationEmployeeRow[] = [];
  public employeesTotal = 0;

  public isLoading = false;
  public isFiltersLoading = false;
  public error: string | null = null;
  public saveError: string | null = null;
  public successMessage: string | null = null;

  private readonly employeesPageLimit = 30;
  // Сколько плашек квалификаций видно в свёрнутой ячейке
  private readonly visibleCodesLimit = 5;
  private employeesOffset = 0;
  private qualificationNames: { [code: string]: string } = {};
  private destroyed$ = new Subject<void>();

  constructor(
    private usersService: UsersService,
    private sectorsService: SectorsService
  ) {}

  ngOnInit() {
    this.loadRegions();
    this.loadQualifications();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onChangeRegion(region: Region | null) {
    this.selectedRegion = region;
    this.selectedBranch = null;
    this.branches = [];
    this.selectedSector = null;
    this.sectors = [];

    if (!region) {
      return;
    }

    this.isFiltersLoading = true;
    this.sectorsService
      .getBranches({ mb_regions: [region.name] })
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (branchesResponse) => {
          this.branches = branchesResponse.data || [];
        },
        () => {
          this.branches = [];
        }
      );
  }

  public onChangeBranch(branch: BranchNames | null) {
    this.selectedBranch = branch;
    this.selectedSector = null;
    this.sectors = [];

    if (!branch) {
      return;
    }

    this.isFiltersLoading = true;
    this.sectorsService
      .getFilterPlots({ mb_ids: [branch.mb_id] })
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (sectorsResponse) => {
          this.sectors = this.sectorsService.makeSectorsNumbers((sectorsResponse.data || []).filter((sector) => sector.is_visible !== 0));
        },
        () => {
          this.sectors = [];
        }
      );
  }

  public onChangeSector(sector: SectorNames | null) {
    this.selectedSector = sector;
  }

  public applyFilter() {
    this.appliedRegion = this.selectedRegion;
    this.appliedBranch = this.selectedBranch;
    this.appliedSector = this.selectedSector;

    this.loadEmployees(false);
  }

  public resetFilter() {
    this.selectedRegion = null;
    this.selectedBranch = null;
    this.selectedSector = null;
    this.branches = [];
    this.sectors = [];

    this.appliedRegion = null;
    this.appliedBranch = null;
    this.appliedSector = null;

    this.loadEmployees(false);
  }

  // Без применённого фильтра сотрудников не грузим — список по всей компании здесь не нужен
  public get isFilterApplied(): boolean {
    return !!(this.appliedRegion || this.appliedBranch || this.appliedSector);
  }

  public get hasMoreEmployees(): boolean {
    return this.employeeRows.length < this.employeesTotal;
  }

  public loadMoreEmployees() {
    this.loadEmployees(true);
  }

  public loadEmployees(loadMore: boolean) {
    const offset = loadMore ? this.employeesOffset : 0;

    if (!this.isFilterApplied) {
      this.employeeRows = [];
      this.employeesTotal = 0;
      this.employeesOffset = 0;
      this.error = null;
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.saveError = null;
    this.successMessage = null;

    this.usersService
      .getUsers(this.makeEmployeesParams(offset))
      .pipe(
        // Квалификации подтягиваем вторым запросом по user_id загруженной
        // страницы — этот метод принимает список через запятую
        switchMap((response) => {
          const count = (response && response['data'] && response['data']['count']) || 0;
          const users = (response && response['data'] && response['data']['result']) || [];
          const rows = users.map((user) => this.toRow(user));
          const userIds = rows.map((row) => row.userId);

          if (!userIds.length) {
            return of({ count: count, rows: rows });
          }

          return this.usersService.getUserQualifications({ user_id: userIds }).pipe(
            map((userQualifications) => {
              this.fillRowCodes(rows, userQualifications || []);
              return { count: count, rows: rows };
            }),
            // Без квалификаций список сотрудников всё равно полезен —
            // наборы просто останутся пустыми, редактор их дозаполнит
            catchError(() => of({ count: count, rows: rows }))
          );
        }),
        takeUntil(this.destroyed$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe(
        ({ count, rows }) => {
          this.employeesTotal = count;
          this.employeesOffset = offset + rows.length;
          this.employeeRows = this.sortRowsByName(loadMore ? this.employeeRows.concat(rows) : rows);
        },
        () => {
          if (!loadMore) {
            this.employeeRows = [];
            this.employeesTotal = 0;
          }
          this.error = 'Не удалось загрузить сотрудников';
        }
      );
  }

  public getVisibleCodes(row: IQualificationEmployeeRow): string[] {
    if (row.isExpanded || !this.canToggleCodes(row)) {
      return row.codes;
    }

    return row.codes.slice(0, this.visibleCodesLimit);
  }

  public canToggleCodes(row: IQualificationEmployeeRow): boolean {
    return row.codes.length > this.visibleCodesLimit;
  }

  public getHiddenCodesCount(row: IQualificationEmployeeRow): number {
    return Math.max(row.codes.length - this.visibleCodesLimit, 0);
  }

  public toggleCodesExpanded(row: IQualificationEmployeeRow) {
    row.isExpanded = !row.isExpanded;
  }

  /**
   * Группа квалификации по префиксу кода — как в легенде спецификации
   * «Работы WO для прода»: 100xxx — новое подключение, 2000xxx — работы
   * по ГП, 3000xxx — другие работы. Неизвестный префикс остаётся без цвета
   */
  public getQualificationChipClass(code: string): string {
    const value = String(code || '');

    if (value.indexOf('2000') === 0) {
      return 'users-qualification__chip--incident';
    }
    if (value.indexOf('3000') === 0) {
      return 'users-qualification__chip--other';
    }
    if (value.indexOf('100') === 0) {
      return 'users-qualification__chip--new-connection';
    }

    return '';
  }

  public getQualificationName(code: string): string {
    return this.qualificationNames[code] || code;
  }

  // «Подтип заявки 1» из спецификации «Работы WO для прода»; пустой список — подсказки нет
  public getQualificationSubtypes(code: string): string[] {
    return QUALIFICATION_SUBTYPES[String(code)] || NO_SUBTYPES;
  }

  // Длинные списки подтипов раскладываются в колонки, чтобы подсказка помещалась в экран
  public getSubtypesClass(subtypes: string[]): string {
    if (subtypes.length > 24) {
      return 'users-qualification__subtypes--cols-3';
    }

    return subtypes.length > 8 ? 'users-qualification__subtypes--cols-2' : '';
  }

  public startEditing(row: IQualificationEmployeeRow) {
    if (row.isSaving || !row.codesLoaded) {
      return;
    }

    this.saveError = null;
    this.successMessage = null;
    row.checkedCodes = {};
    row.codes.forEach((code) => (row.checkedCodes[code] = true));
    row.isEditing = true;
  }

  public cancelEditing(row: IQualificationEmployeeRow) {
    if (row.isSaving) {
      return;
    }

    row.isEditing = false;
  }

  public checkAllCodes(row: IQualificationEmployeeRow) {
    if (row.isSaving) {
      return;
    }

    this.qualifications.forEach((qualification) => (row.checkedCodes[qualification.id] = true));
  }

  // Только снимает галочки: удаление произойдёт при сохранении полного набора
  public uncheckAllCodes(row: IQualificationEmployeeRow) {
    if (row.isSaving) {
      return;
    }

    row.checkedCodes = {};
  }

  public toggleCode(row: IQualificationEmployeeRow, code: string, isChecked: boolean) {
    row.checkedCodes[code] = isChecked;
  }

  public getCheckedCodes(row: IQualificationEmployeeRow): string[] {
    return this.qualifications.map((qualification) => qualification.id).filter((code) => !!row.checkedCodes[code]);
  }

  public saveRow(row: IQualificationEmployeeRow) {
    if (row.isSaving) {
      return;
    }

    const checkedCodes = this.getCheckedCodes(row);

    row.isSaving = true;
    this.saveError = null;
    this.successMessage = null;

    this.usersService
      .actualizeUserQualifications(row.userId, checkedCodes)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (row.isSaving = false))
      )
      .subscribe(
        () => {
          row.codes = checkedCodes;
          row.isEditing = false;
          this.successMessage = `Квалификации сотрудника ${row.name} сохранены`;
        },
        () => {
          this.saveError = `Не удалось сохранить квалификации сотрудника ${row.name}`;
        }
      );
  }

  private makeEmployeesParams(offset: number) {
    // На экране квалификаций нужны только сотрудники группы 4 в статусе 1 (условие задачи)
    const params: any = {
      limit: this.employeesPageLimit,
      offset: offset,
      groups_ids: [QUALIFICATION_EMPLOYEES_GROUP_ID],
      employee_status: QUALIFICATION_EMPLOYEE_STATUS,
    };

    if (this.appliedRegion) {
      params.mb_regions = [this.appliedRegion.name];
    }
    if (this.appliedBranch) {
      params.mb_ids = [this.appliedBranch.mb_id];
    }
    if (this.appliedSector) {
      params.sectors_ids = [this.appliedSector.sector_id];
    }

    return params;
  }

  // Бэкенд отдаёт сотрудников не по алфавиту, поэтому весь загруженный список сортируем по ФИО на клиенте
  private sortRowsByName(rows: IQualificationEmployeeRow[]): IQualificationEmployeeRow[] {
    return rows.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
  }

  private toRow(user): IQualificationEmployeeRow {
    const employee = (user && user.employee) || {};
    const fullName = [employee.last_name, employee.first_name, employee.patronymic_name].filter((part) => !!part).join(' ');

    return {
      userId: Number(user && user.u_id),
      name: fullName || String(user && user.u_id),
      codes: [],
      codesLoaded: false,
      isExpanded: false,
      isEditing: false,
      isSaving: false,
      checkedCodes: {},
    };
  }

  private fillRowCodes(rows: IQualificationEmployeeRow[], userQualifications: IUserQualification[]) {
    const rowsByUserId: { [userId: string]: IQualificationEmployeeRow } = {};

    rows.forEach((row) => {
      rowsByUserId[String(row.userId)] = row;
      // Ответ получен: пустой набор с этого момента — настоящий, а не «не загрузилось»
      row.codesLoaded = true;
    });

    userQualifications.forEach((item) => {
      const row = rowsByUserId[String(item && item.user_id)];
      const workTypeId = item && item.work_type_id;
      const code = workTypeId !== null && workTypeId !== undefined ? String(workTypeId) : '';

      if (row && code && row.codes.indexOf(code) === -1) {
        row.codes.push(code);
      }
    });
  }

  private loadRegions() {
    this.isFiltersLoading = true;
    this.sectorsService
      .getRegions()
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => (this.isFiltersLoading = false))
      )
      .subscribe(
        (regionsResponse) => {
          this.regions = (regionsResponse.data || []).map((name) => ({ name }));
        },
        () => {
          this.regions = [];
        }
      );
  }

  private loadQualifications() {
    this.usersService
      .getQualifications()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (qualifications) => {
          this.qualifications = qualifications || [];
          this.qualificationNames = this.qualifications.reduce(
            (acc, qualification) => {
              acc[qualification.id] = qualification.name;
              return acc;
            },
            {} as { [code: string]: string }
          );
          // Справочник нужен и таблице, и редактору — сотрудников грузим после него
          this.loadEmployees(false);
        },
        () => {
          this.error = 'Не удалось загрузить справочник квалификаций';
        }
      );
  }
}
